import React, { useState } from "react";
import { DateField } from "@/components/DateField";
import { Mail, RefreshCw, Undo2 } from "lucide-react";
import { ReceiptPdfButton } from "@/components/ReceiptPdf";
import { Input } from "@/components/ui-kit";
import { fmtMoney } from "@/components/EventCard";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDateTime } from "@/lib/time";

export type Cur = "IQD" | "USD";
export interface LedgerEntry {
  entry_id: string; kind: "fee" | "exemption" | "debit_adj" | "credit_adj" | "payment"; currency: Cur; amount: string; note?: string;
  booking_id?: string; fee_model?: string; event_title_ar?: string; event_title_en?: string; guests?: number; per_person?: string; by: string;
  created_at: string; effective_at?: string; method?: string; reference?: string; reverses?: string; reversed_by?: string;
  settlement?: { currency: Cur; amount: string; rate: string; direction: string; approved_by: string };
  receipt_no?: string; receipt_email?: { status: string; sent_to?: string[]; failed_to?: string[]; skipped_demo?: string[] };
}
export interface StatementData {
  currencies: Partial<Record<Cur, Record<string, string>>>; entries: LedgerEntry[];
}

const ROWS: [string, string][] = [["opening", ""], ["fees", "+"], ["debit_adj", "+"], ["exemptions", "−"], ["credit_adj", "−"], ["payments", "−"]];
const m = (amount: string, currency: Cur) => <bdi>{fmtMoney({ amount, currency } as any)}</bdi>;

export const DateRange = ({ onApply }: { onApply: (from: string, to: string) => void }) => {
  const { t } = usePrefs();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  return (
    <div className="flex flex-wrap items-end gap-2 mb-4">
      <label className="text-xs font-bold space-y-1">{t("b7.from")}<DateField value={from} max={to || undefined} clearable onChange={setFrom} testId="statement-from" /></label>
      <label className="text-xs font-bold space-y-1">{t("b7.to")}<DateField value={to} min={from || undefined} clearable onChange={setTo} testId="statement-to" /></label>
      <button onClick={() => onApply(from, to)} data-testid="statement-apply" className="h-10 px-5 rounded-full bg-foreground text-background text-sm font-bold">{t("b7.apply")}</button>
    </div>
  );
};

export const CurrencyCards = ({ data }: { data: StatementData }) => {
  const { t } = usePrefs();
  const list = Object.entries(data.currencies) as [Cur, Record<string, string>][];
  if (!list.length) return <p data-testid="statement-empty" className="text-muted-foreground py-6">{t("b7.noEntries")}</p>;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {list.map(([c, v]) => (
        <div key={c} data-testid={`statement-${c}`} className="rounded-3xl border bg-card p-5 space-y-1.5">
          <p className="font-display text-lg font-black">{c}</p>
          {ROWS.map(([k, sign]) => (
            <p key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{sign} {t(`b7.${k}`)}</span><span data-testid={`statement-${c}-${k}`} className="font-semibold">{m(v[k], c)}</span></p>
          ))}
          <p className="flex justify-between border-t pt-2 font-black"><span>= {t("b7.closing")}</span><span data-testid={`statement-${c}-closing`}>{m(v.closing, c)}</span></p>
        </div>
      ))}
      <p className="text-xs text-muted-foreground md:col-span-2">{t("b7.formula")}</p>
    </div>
  );
};

export const EntriesList = ({ entries, onReverse, onResend, name = "", highlight }: { entries: LedgerEntry[]; onReverse?: (e: LedgerEntry) => void; onResend?: (e: LedgerEntry) => void; name?: string; highlight?: string | null }) => {
  const { t, lang } = usePrefs();
  if (!entries.length) return null;
  const minus = (k: string) => ["exemption", "credit_adj", "payment"].includes(k);
  return (
    <div data-testid="ledger-entries" className="mt-6 divide-y rounded-3xl border bg-card">
      {entries.map((e) => (
        <div key={e.entry_id} id={`entry-${e.entry_id}`} data-testid={`ledger-${e.entry_id}`} className={`p-4 flex flex-wrap items-start justify-between gap-3 transition-[background-color] ${highlight === e.entry_id ? "bg-primary/10" : ""}`}>
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-bold">
              {t(`b7.kind.${e.kind}`)} {e.reverses && <span className="text-xs text-amber-600">· {t("b7.reversal")}</span>}
              {e.reversed_by && <span className="text-xs text-muted-foreground"> · {t("b7.reversed")}</span>}
            </p>
            {e.event_title_ar && <p className="text-xs text-muted-foreground">{lang === "ar" ? e.event_title_ar : e.event_title_en}{e.fee_model ? ` · ${t(`fm.${e.fee_model}`)}` : ` · ${e.guests} × ${e.per_person} ${e.currency}`}</p>}
            {e.note && <p className="text-xs">{t(`b7.note.${e.note}`) !== `b7.note.${e.note}` ? t(`b7.note.${e.note}`) : e.note}</p>}
            {(e.method || e.reference) && <p className="text-xs text-muted-foreground">{[e.method, e.reference].filter(Boolean).join(" · ")}</p>}
            {e.settlement && <p className="text-xs text-muted-foreground">{t("b7.settled")} {m(e.settlement.amount, e.settlement.currency)} · {e.settlement.rate} ({e.settlement.direction}) · {e.settlement.approved_by}</p>}
            <p className="text-[11px] text-muted-foreground">{fmtDateTime(e.effective_at || e.created_at, lang)} · {e.by}</p>
            {e.receipt_no && (
              <div className="flex flex-wrap items-center gap-2 pt-1.5" data-testid={`receipt-row-${e.entry_id}`}>
                <span className="text-xs font-bold">{t("rc.receipt")} <bdi dir="ltr">{e.receipt_no}</bdi></span>
                <ReceiptPdfButton e={e} name={name} />
                {onResend && e.receipt_email && (
                  <span data-testid={`receipt-email-status-${e.entry_id}`} className={`text-[11px] font-bold rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${e.receipt_email.status === "sent" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : e.receipt_email.status === "sending" ? "bg-accent" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`}>
                    <Mail className="h-3 w-3" />{t(`rc.email.${e.receipt_email.status}`)}{e.receipt_email.sent_to?.length ? ` · ${e.receipt_email.sent_to.length}` : ""}
                  </span>
                )}
                {onResend && <button onClick={() => onResend(e)} data-testid={`receipt-resend-${e.entry_id}`} className="h-8 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1 hover:bg-accent"><RefreshCw className="h-3.5 w-3.5" />{t("rc.resend")}</button>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-black ${minus(e.kind) ? "text-emerald-600" : ""}`}><bdi dir="ltr">{minus(e.kind) ? "−" : "+"}{fmtMoney({ amount: e.amount, currency: e.currency } as any)}</bdi></span>
            {onReverse && !e.reverses && !e.reversed_by && e.kind !== "fee" && e.kind !== "exemption" && (
              <button onClick={() => onReverse(e)} data-testid={`ledger-reverse-${e.entry_id}`} aria-label={t("b7.reverse")} className="h-8 w-8 rounded-full border grid place-items-center hover:bg-accent"><Undo2 className="h-4 w-4" /></button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
